/**
 * Configuration for frontend table column compositions.
 */

/**
 * Defines the structure for a single composition column.
 */
export interface CompositionDefinition {
  compositionName: string; // Display name for the composition header
  primaryCode: string; // The main constituent column code, potentially used for sorting or emphasis
  constituentCodes: string[]; // Array of original column codes that make up this composition
  // Optional: Add rendering hints or component names later if needed
}

/**
 * Main configuration object mapping internal keys to composition definitions.
 * Column codes are based on story-2 and verified against column_list.text.
 */
export const compositionConfig: Record<string, CompositionDefinition> = {
  productInfo: {
    compositionName: 'Product Info',
    primaryCode: 'Item[Item Description]',
    constituentCodes: [
      'image', // Add product image
      // Core Identifiers
      'product_status',
      'Item[Item Description]',
      'variant',
      'by_variant',
      'product_sku',
      'product_barcode',
      // Display Helpers
      'variant_image', // Add variant image
      // Options
      'variant_option1',
      'variant_option2',
      'variant_option3',
      // Associated Info
      'Vendor Name', // Standardized vendor name
      'product_type', // Add product type
      'product_vendor', // Allow alias
    ],
  },
  pricing: {
    compositionName: 'Pricing',
    primaryCode: 'unit_price',
    constituentCodes: [
      'unit_price', // Base selling price
      '[Selling Price]', // Allow alias/alternative
      'unit_cost',
      'variant_unit_cost',
      '[average_unit_cost]', // Allow alias/alternative
      '[unit_cost]', // Ensure base unit cost is included if different alias
      'original_price',
      '[original_price]', // Allow alias/alternative
      '[product_profit_margin]', // Margin %
    ],
  },
  inventoryLevels: {
    compositionName: 'Inventory Levels',
    primaryCode: '[Actual Inventory Quantity]', // Often represents 'Available'
    constituentCodes: [
      // Core Levels
      '[Actual Inventory Quantity]',
      '[on_hand_quantity]', // Explicit On Hand
      '[available_quantity]', // Explicit Available (if different from Actual)
      '[committed_quantity]', // Committed stock
      '[inventory_quantity_percent]',

      // Planning & Incoming
      '[incoming_inventory_quantity]',
      '[incoming_quantity]', // Allow alias
      '[stock_days_inventory_planning]', // Days Remaining
      '[safety_stock_level]',
    ],
  },
  inventoryValues: {
    compositionName: 'Inventory Values',
    primaryCode: '[Actual Inventory Cost]', // Use cost as primary
    constituentCodes: [
      // Core Levels
      '[inventory_value]',
      '[Actual Inventory Cost]',
      '[inventory_value_percent]',
      '[Retail Value]',
      // Profitability linked to value
      '[estimated_gross_profit]',
      '[estimated_gross_profit_margin]',
    ],
  },
  salesFigures: {
    compositionName: 'Sales Figures',
    primaryCode: '[Net Sales]',
    constituentCodes: [
      '[Net Sales]',
      '[Gross Amount]',
      '[Gross Sales]', // Allow alias
      '[net_quantity_sold]',
      '[Quantity Sold]', // Allow alias
      '[Order Count]',
    ],
  },
  profitability: {
    compositionName: 'Profitability',
    primaryCode: '[Profit Margin]', // This is Gross Profit Amount
    constituentCodes: [
      '[Profit Margin]', // Gross Profit Amount
      '[gross_profit]', // Allow alias/alternative
      '[Profit Margin %]',
      '[cogs]', // Cost of Goods Sold
    ],
  },
  adjustmentsFees: {
    compositionName: 'Adjustments & Fees',
    primaryCode: '[Discount]',
    constituentCodes: [
      '[Discount]',
      '[Return Amount]',
      '[Shipping Amount]',
      '[Tax Amount]',
      '[duty_amount]',
      '[additional_fee]',
      '[return_fee]',
      '[Tip Amount]',
    ],
  },
  replenishmentDetails: {
    compositionName: 'Replenishment',
    primaryCode: '[inventory_replenishment]',
    constituentCodes: [
      // Core Replenishment
      '[inventory_replenishment]', // Reorder Qty
      '[reorder_date]',
      '[reorder_priority]',
      '[days_to_reorder]',
      '[reorder_quantity]',
      '[reorder_point]',
      // Associated Financials
      '[replenishment_inventory_cost]',
      '[replenishment_inventory_retail_value]',
      '[replenishment_inventory_gross_profit]',
    ],
  },
  forecastSummary: {
    compositionName: 'Forecast Summary',
    primaryCode: '[forecast_sales_quantity]',
    constituentCodes: [
      // Core Forecast
      '[forecast_sales_quantity]',
      '[sales_forecast]', // Allow alias from sales forecast report
      '[forecast_sales_amount]',
      '[inventory_forecasting]', // Qty Need
      '[forecast_over_under_stock]', // Over/Under Qty
      // Lost Sales
      '[forecast_lost_sales_quantity]',
      '[forecast_lost_sales_amount]',
      '[average_daily_units_sold_forecast]',
    ],
  },
  reorderParams: {
    compositionName: 'Reorder Params',
    primaryCode: 'supplier',
    constituentCodes: [
      // Supplier Info
      'supplier',
      'supplier_id',
      '[is_preferred]',
      // Timing
      '[vendor_lead_time]',
      '[reorder_cycle]',
      // Order Quantities
      '[min_order_quantity]',
      '[max_order_quantity]',
      '[fixed_order_quantity]',
      // Variant specific Order Quantities
      '[variant_min_order_quantity]',
      '[variant_max_order_quantity]',
      '[variant_fixed_order_quantity]',
      // Case Info
      '[case_quantity]',
      '[reorder_case_quantity]',
    ],
  },
  inventoryParams: {
    compositionName: 'Inventory Params',
    primaryCode: '[min_inventory_quantity]',
    constituentCodes: [
      // Stock Levels
      '[min_inventory_quantity]',
      '[max_inventory_quantity]',
      // Variant Specific Stock Levels
      '[variant_min_inventory_quantity]',
      '[variant_max_inventory_quantity]',
      // Cover Days
      '[min_stock_cover_days]',
      '[max_stock_cover_days]',
      // Exclusion
      '[replenishment_exclusion]',
    ],
  },
  salesVelocityHistory: {
    compositionName: 'Sales Velocity & History',
    primaryCode: '[average_daily_units_sold_param]',
    constituentCodes: [
      // Sales Rate
      '[average_daily_units_sold_param]', // Avg Daily Sold (Param period)
      '[average_daily_units_sold_sales_trend]', // Avg Daily Sold (Trend adjusted)
      '[avg_unit_sold_inventory_planning]', // Avg Daily Sold (Inv Planning basis)
      // Total Sold
      '[sold_quantity_param]', // Total Sold (Param period)
      '[units_sold_inventory_planning]', // Total Sold (Inv Planning basis)
      // Stock Days
      '[out_of_stock_days]',
      '[in_stock_days]',
    ],
  },
  // --- START: New compositions for inventory_forecasting_stock_movement ---
  inventoryMovementSummary: {
    compositionName: 'Inventory Movement',
    primaryCode: '[ending_quantity]',
    constituentCodes: [
      '[beginning_quantity]',
      '[added_inventory_quantity]',
      '[deducted_inventory_quantity]',
      '[ending_quantity]',
    ],
  },
  currentStockStatus: {
    compositionName: 'Current Stock',
    primaryCode: '[Actual Inventory Quantity]',
    constituentCodes: [
      '[Actual Inventory Quantity]',
      '[incoming_quantity]', // From stock movement columns
    ],
  },
  dailyMovementRate: {
    compositionName: 'Daily Movement Rate',
    primaryCode: '[average_daily_units_deducted]',
    constituentCodes: [
      '[average_daily_units_deducted]',
      '[average_daily_units_deducted_sales_trend]',
    ],
  },
  stockMovementForecast: {
    compositionName: 'Stock Movement Forecast',
    primaryCode: '[inventory_forecasting_stock_movement]',
    constituentCodes: [
      '[forecast_time]',
      '[inventory_forecasting_stock_movement]',
    ],
  },
  // --- END: New compositions ---
};

/**
 * Returns a list of composition keys applicable to the given report context (measure code).
 * This determines which compositions are potentially available for display in Composition View.
 * The actual display depends on whether the required constituent columns are selected by the user.
 * @param measureCode - The measure code of the current report.
 * @returns An array of applicable composition keys (e.g., ['productInfo', 'pricing', 'salesFigures']).
 */
export function getAvailableCompositionKeys(measureCode: string): string[] {

  // Define sets of keys for different report contexts
  // Note: These lists can be adjusted based on desired default compositions for each report type
  const measureCodeKeys = {
    // --- Sales Focused Reports --- //
    'net_sales': ['productInfo', 'pricing', 'salesFigures', 'profitability', 'salesVelocityHistory', 'adjustmentsFees', 'inventoryLevels', 'inventoryValues'], // Ordered by importance
    'gross_profit': ['productInfo', 'pricing', 'salesFigures', 'profitability', 'salesVelocityHistory', 'adjustmentsFees', 'inventoryLevels', 'inventoryValues'], // Ordered by importance
    'orders': ['productInfo', 'pricing', 'salesFigures', 'profitability', 'salesVelocityHistory', 'adjustmentsFees', 'inventoryLevels', 'inventoryValues'], // Ordered by importance
    'sales_overview': ['productInfo', 'pricing', 'salesFigures', 'profitability', 'salesVelocityHistory', 'adjustmentsFees', 'inventoryLevels', 'inventoryValues'], // Ordered by importance
    'sell_through_rate': ['productInfo', 'pricing', 'salesFigures', 'inventoryLevels', 'salesVelocityHistory', 'inventoryMovementSummary'], // Ordered by importance

    // --- Inventory / Replenishment / Forecast Focused Reports --- //
    'inventory_replenishment': [
      'productInfo',
      'inventoryLevels',
      'replenishmentDetails', // Key info for this report
      'salesVelocityHistory', // Context
      'forecastSummary',
      'pricing', // Relevant cost/price
      'reorderParams',
      'inventoryParams',
      'inventoryValues',
    ],
    'inventory_forecasting': [
      'productInfo',
      'forecastSummary', // Key info
      'inventoryLevels',
      'inventoryValues',
      'salesVelocityHistory',
      'pricing',
    ],
    'sales_forecast': [ // Similar to inventory_forecasting
      'productInfo',
      'forecastSummary', // Key info
      'inventoryLevels',
      'inventoryValues',
      'salesVelocityHistory',
      'pricing',
    ],
    'inventory_planning': [
      'productInfo',
      'inventoryLevels',
      'inventoryValues',
      'salesVelocityHistory',
      'pricing',
      'reorderParams',
      'inventoryParams',
    ],
    'replenishment_setting': [ // Settings focused
        'productInfo',
        'reorderParams', // Primary focus
        'inventoryParams', // Primary focus
        'pricing', // Relevant cost/price
    ],
    'Actual Inventory Quantity': [
        'productInfo',
        'inventoryLevels', // Primary focus
        'inventoryValues',
        'inventoryParams',
        'pricing', // Relevant cost/price
    ],
    'inventory_movement': [ // Assuming this is similar to stock_movement
        'productInfo',
        'inventoryLevels', // Primary focus
        'inventoryValues',
        'inventoryMovementSummary', // Add specific movement group
        'currentStockStatus',
        'pricing', // Relevant cost/price
    ],
     'stock_movement': [ // Alias for inventory_movement or similar report
        'productInfo',
        'pricing', // Cost context
        'inventoryLevels', // Primary focus
        'inventoryValues',
        'inventoryMovementSummary', // Add specific movement group
        'currentStockStatus',
    ],
    // --- START: Add mapping for inventory_forecasting_stock_movement ---
    'inventory_forecasting_stock_movement': [
        'productInfo',
        'inventoryMovementSummary', // Key info
        'currentStockStatus', // Key info
        'stockMovementForecast', // Key info
        'dailyMovementRate', // Context
        'pricing', // Cost context
    ],
    // --- END: Add mapping ---
    'inventory_optimization': [
        'productInfo',
        'pricing',
        'inventoryLevels',
        'inventoryValues',
        'salesVelocityHistory',
        'reorderParams',
        'inventoryParams',
    ],
    'inventory_turnover': [
        'productInfo',
        'pricing',
        'inventoryLevels',
        'inventoryValues',
        'salesVelocityHistory',
    ],
    'inventory_turnover_days': [
        'productInfo',
        'pricing',
        'inventoryLevels',
        'inventoryValues',
        'salesVelocityHistory',
    ],
    // Add more measure codes as needed
  };

  // Default keys for reports not explicitly mapped - Ordered by likely importance
  const allKeys = [
    'productInfo',
    'pricing',
    'inventoryLevels',
    'inventoryValues',
    'salesFigures',
    'salesVelocityHistory',
    'replenishmentDetails',
    'forecastSummary',
    'profitability',
    'adjustmentsFees',
    'reorderParams',
    'inventoryParams',
    // Add new ones to default? Maybe not, keep default lean.
    'inventoryMovementSummary',
    'currentStockStatus',
    'dailyMovementRate',
    'stockMovementForecast',
  ];

  // Return specific keys if measureCode is mapped, otherwise return commonKeys
  if (measureCodeKeys[measureCode as keyof typeof measureCodeKeys]) {
    return measureCodeKeys[measureCode as keyof typeof measureCodeKeys];
  }

  // Fallback to basic keyword matching if specific map fails (optional)
  const lowerMeasureCode = measureCode.toLowerCase();
  if (lowerMeasureCode.includes('sale') || lowerMeasureCode.includes('profit') || lowerMeasureCode.includes('order')) {
    return ['productInfo', 'pricing', 'salesFigures', 'profitability', 'adjustmentsFees'];
  }
  if (lowerMeasureCode.includes('inventory') || lowerMeasureCode.includes('stock') || lowerMeasureCode.includes('replenish') || lowerMeasureCode.includes('forecast')) {
     // Add new relevant ones to this fallback too
     return ['productInfo', 'pricing', 'inventoryLevels', 'inventoryValues', 'reorderParams', 'inventoryParams', 'salesVelocityHistory', 'inventoryMovementSummary', 'currentStockStatus', 'dailyMovementRate', 'stockMovementForecast'];
  }

  // Return all defined keys as a last resort
  return Object.keys(compositionConfig); // Return all keys if no better match
}

// Type helper to get keys as string literals if needed elsewhere
export type CompositionKey = keyof typeof compositionConfig;
